import { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { MiButton } from "@miplatform/components";
import {
  APP_SIDE_NAV_WIDTH,
  APP_SIDE_NAV_WIDTH_COLLAPSED,
  APP_SUB_NAV_WIDTH_COLLAPSED,
  NAV_ITEM_GROUPS,
  PERMISSIONS,
} from "@miplatform/constants";
import { useOutsideClickListener, useRouter } from "@miplatform/hooks";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Divider, useMediaQuery } from "@mui/material";
import { SideNavGroupHeader } from "./SideNavGroupHeader";
import { SideNavItem } from "./SideNavItem";
import { SubNavDrawer } from "./SubNavDrawer";

/**
 * Safely serializes navItems array by removing circular references and React elements
 * @param {Array} navItems - The navItems array to serialize
 * @returns {Array} Clean array that can be JSON.stringify'd
 */
const serializeNavItems = (navItems) => {
  return navItems.map((navItem) => {
    const cleanItem = {
      items: navItem.items ? navItem.items : [],
    };

    // if there are sub-items, recursively clean them too
    if (navItem.items?.length) {
      cleanItem.items = serializeNavItems(navItem.items);
    }

    return cleanItem;
  });
};

/**
 * Finds the active navigation item based on the current pathname
 * @param {Array} navItems - Array of navigation items to search through
 * @param {string} pathname - Current pathname to match against
 * @returns {Object|undefined} The active nav item or undefined if no match
 */
const findActiveModule = (navItems, pathname) => {
  const urlSegments = pathname.split("/");
  let moduleSegment = `/${urlSegments[1]}`;
  if (urlSegments[1] === "config") {
    moduleSegment += `/${urlSegments[2]}`;
  }
  return navItems.find((navItem) => {
    return moduleSegment.includes(navItem.path);
  });
};

/**
 * A collapsible side navigation component that displays grouped navigation items
 * with expandable modules and sub-navigation functionality.
 *
 * @component
 * @param {Object} props - The component props
 * @param {Array} [props.navItems=[]] - Array of navigation items to display, each containing:
 *   - name: string - Display name of the nav item
 *   - path: string - Route path for the nav item
 *   - icon: string - Icon identifier for the nav item
 *   - group: string - Group category for organizing nav items
 *   - items: Array - Optional sub-navigation items for modules
 *   - end: boolean - Whether this is an end route for exact matching
 *   - testId: string - Test identifier for the nav item
 *
 * @returns {JSX.Element} A side navigation component with main nav, sub-nav drawer,
 *   collapse/expand functionality, and permission-based settings section
 */
export function AppSideNav({ navItems = [] }) {
  const { location } = useRouter();

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isSubNavCollapsed, setIsSubNavCollapsed] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);

  const mainRef = useRef(null);
  const subRef = useRef(null);

  // memoized callback to ensure location.pathname is up to date
  const handleOutsideClick = useCallback(() => {
    const activeNavItem = findActiveModule(navItems, location.pathname);
    setSelectedModule(activeNavItem);

    if (!activeNavItem?.items?.length) {
      setIsSubNavCollapsed(true);
    }
  }, [navItems, location.pathname]);

  useOutsideClickListener(handleOutsideClick, [mainRef, subRef]);

  const { hasPermission } = usePermissions();
  const showSettings = [PERMISSIONS.USER.READ, PERMISSIONS.ROLE.READ].some(
    hasPermission
  );

  const getSideNavWidth = () => {
    const mainNavWidth = isCollapsed
      ? APP_SIDE_NAV_WIDTH_COLLAPSED
      : APP_SIDE_NAV_WIDTH;

    let subNavWidth = APP_SUB_NAV_WIDTH_COLLAPSED;

    if (selectedModule && !isSubNavCollapsed) {
      subNavWidth = APP_SIDE_NAV_WIDTH;
    }

    return mainNavWidth + subNavWidth;
  };

  const { classes, theme } = useStyles({
    isCollapsed,
    width: APP_SIDE_NAV_WIDTH,
    widthCollapsed: APP_SIDE_NAV_WIDTH_COLLAPSED,
    totalWidth: getSideNavWidth(),
  });

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (isSmallScreen) {
      setIsCollapsed(true);
    }
  }, [isSmallScreen]);

  // set initial selected module based on current path when landing on page
  useEffect(() => {
    // find the nav item that matches the current pathname
    const activeNavItem = findActiveModule(navItems, location.pathname);

    if (activeNavItem) {
      setSelectedModule(activeNavItem);
      setIsSubNavCollapsed(!activeNavItem.items?.length);
    }
  }, [JSON.stringify(serializeNavItems(navItems), null, 2), location.pathname]);

  useEffect(() => {
    // upon switching from MiBuild to MiConfig, expand main nav
    if (location.pathname === "/config") {
      setIsCollapsed(false);
    }
  }, [location.pathname]);

  const toggleCollapse = () => setIsCollapsed((prev) => !prev);

  const handleNavItemClick = (navItem) => {
    setSelectedModule(navItem);

    // for nav items containing items (modules)
    if (navItem.items?.length) {
      // if clicking on the currently selected module, toggle main nav collapse
      if (navItem.name === selectedModule?.name) {
        toggleCollapse();
      } else {
        setIsCollapsed(false);
      }
      setIsSubNavCollapsed(false);
    } else {
      // for nav items without items (Homes, Communities, etc), collapse both
      setIsCollapsed(true);
      setIsSubNavCollapsed(true);
    }
  };

  const isNavItemActive = (navItem) => {
    const isChildActive = navItem.items?.some((child) =>
      location.pathname.includes(child.path)
    );
    const isDirectPathActive = location.pathname.startsWith(navItem.path);
    const isPathActive = navItem.items?.length
      ? isChildActive
      : isDirectPathActive;
    const isSelectedModule = selectedModule?.name === navItem.name;

    return isSelectedModule || (isPathActive && !selectedModule);
  };

  const groupedNavItems = Object.groupBy(navItems, (item) => item.group);
  const groupedSubNavItems = Object.groupBy(
    selectedModule?.items || [],
    (item) => (item.group ? item.group : NAV_ITEM_GROUPS.NO_GROUP_NAME)
  );

  const renderGroupedNavItems = (groupName) => {
    return (
      <>
        {groupedNavItems[NAV_ITEM_GROUPS[groupName]]?.length && (
          <SideNavGroupHeader
            groupName={NAV_ITEM_GROUPS[groupName]}
            isMainNavCollapsed={isCollapsed}
          />
        )}
        {(groupedNavItems[NAV_ITEM_GROUPS[groupName]] ?? []).map(
          (navItem, index) => {
            return (
              <SideNavItem
                key={`${navItem.path}-${index}`}
                icon={navItem.icon}
                name={navItem.name}
                path={navItem.path}
                end={navItem.end}
                testId={navItem.testId}
                items={navItem.items}
                isCollapsed={isCollapsed}
                isActive={isNavItemActive(navItem)}
                onClick={() => handleNavItemClick(navItem)}
                hasChildItems={!!navItem.items?.length}
                disabled={navItem.disabled}
              />
            );
          }
        )}
      </>
    );
  };

  return (
    <Box className={classes.root} ref={mainRef}>
      <Box className={classes.mainNavContainer} data-testid="app-side-nav">
        <Box className={classes.navItemsContainer}>
          {/* MODULES */}
          {renderGroupedNavItems(NAV_ITEM_GROUPS.MODULES)}

          {/* GENERAL */}
          {renderGroupedNavItems(NAV_ITEM_GROUPS.GENERAL)}

          {/* ADMIN */}
          {renderGroupedNavItems(NAV_ITEM_GROUPS.ADMIN)}
        </Box>
        <Box className={classes.footer}>
          <Divider
            sx={{
              margin: theme.spacing(4, 2, 1, 2),
            }}
          />
          {/* SETTINGS */}
          {showSettings
            ? (groupedNavItems[NAV_ITEM_GROUPS.SETTINGS] ?? []).map(
                (
                  { icon, name, path, end = false, items, testId, disabled },
                  index
                ) => (
                  <SideNavItem
                    key={`${path}-${index}`}
                    icon={icon}
                    name={name}
                    path={path}
                    end={end}
                    testId={testId}
                    items={items}
                    isCollapsed={isCollapsed}
                    onClick={() => setSelectedModule(null)}
                    disabled={disabled}
                  />
                )
              )
            : null}
          <Box className={classes.collapseSection}>
            <MiButton
              data-testid="collapse-and-expand-icon"
              className={classes.footerIcon}
              startIcon={{
                path: isCollapsed ? mdiChevronRight : mdiChevronLeft,
                iconColor: theme.palette.grey.medium,
                iconSize: 1,
              }}
              onClick={toggleCollapse}
              disableRipple
            />
          </Box>
        </Box>
      </Box>
      <SubNavDrawer
        ref={subRef}
        isMainNavCollapsed={isCollapsed}
        selectedModule={selectedModule}
        groupedNavItems={groupedSubNavItems}
        isSubNavCollapsed={isSubNavCollapsed}
        setIsCollapsed={setIsCollapsed}
        setIsSubNavCollapsed={setIsSubNavCollapsed}
        disableExpand={!selectedModule?.items?.length}
      />
    </Box>
  );
}

const useStyles = makeStyles()(
  (theme, { isCollapsed, width, widthCollapsed, totalWidth }) => ({
    root: {
      display: "flex",
      height: "100%",
      width: `${totalWidth}px`,
      transition: "width 250ms ease-in-out",
    },
    mainNavContainer: {
      width: isCollapsed ? widthCollapsed : width,
      padding: theme.spacing(0, 2),
      backgroundColor: theme.palette.common.white,
      borderRight: `1px solid ${theme.palette.grey.mediumLight}`,
      display: "flex",
      justifyContent: "space-between",
      flexDirection: "column",
      overflowY: "auto",
      transition: "all 200ms ease-in-out",
    },
    navItemsContainer: {
      overflowY: "auto",
      overflowX: "hidden",
    },
    footer: {
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minHeight: 118,
    },
    collapseSection: {
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: isCollapsed ? "center" : "flex-end",
    },
  })
);
